import { Fragment, type RefObject, type UIEventHandler } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { TERMS_CONTENT_BLOCKS, type TermsContentBlock, type TermsTextRun } from '@/lib/terms';
import { cn } from '@/lib/utils';

interface TermsAcceptanceContentProps {
  checkboxId: string;
  hasAcceptedTerms: boolean;
  hasScrolledToBottom: boolean;
  isSubmitting: boolean;
  onAcceptedTermsChange: (checked: boolean) => void;
  onTermsScroll: UIEventHandler<HTMLDivElement>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  acceptanceLabel?: string;
  helperText?: string;
  scrollContainerTestId?: string;
}

function getTermsBlockClassName(type: Exclude<TermsContentBlock['type'], 'spacer'>) {
  switch (type) {
    case 'title':
      return 'font-display text-xl leading-7 text-night-900';
    case 'date':
      return 'font-body text-base italic leading-6 text-night-700';
    case 'heading':
      return 'pt-2 font-display text-sm font-semibold leading-6 text-night-900';
    case 'paragraph':
      return 'font-body text-sm leading-6 text-night-700';
  }
}

function renderTextWithLineBreaks(text: string, keyPrefix: string) {
  return text.split('\n').map((line, lineIndex) => (
    <Fragment key={`${keyPrefix}-${lineIndex}`}>
      {lineIndex > 0 && <br />}
      {line}
    </Fragment>
  ));
}

function renderTextRun(run: TermsTextRun, runIndex: number) {
  const className = cn(
    run.bold && 'font-semibold',
    run.italic && 'italic',
    run.underline && 'underline',
    run.href && 'text-blueviolet underline'
  );
  const content = renderTextWithLineBreaks(run.text, `terms-run-${runIndex}`);

  if (run.href) {
    return (
      <a key={runIndex} href={run.href} target="_blank" rel="noreferrer" className={className}>
        {content}
      </a>
    );
  }

  if (className) {
    return (
      <span key={runIndex} className={className}>
        {content}
      </span>
    );
  }

  return <Fragment key={runIndex}>{content}</Fragment>;
}

export function TermsAcceptanceContent({
  checkboxId,
  hasAcceptedTerms,
  hasScrolledToBottom,
  isSubmitting,
  onAcceptedTermsChange,
  onTermsScroll,
  scrollContainerRef,
  acceptanceLabel = 'I have read and agree to these Terms and Conditions.',
  helperText = 'Scroll to the bottom of the terms to enable the agreement checkbox.',
  scrollContainerTestId,
}: TermsAcceptanceContentProps) {
  return (
    <>
      <div
        ref={scrollContainerRef}
        data-testid={scrollContainerTestId}
        onScroll={onTermsScroll}
        className="my-3 max-h-[50vh] overflow-y-auto rounded-md border border-night-100 bg-lilac-100 p-4"
      >
        <div className="space-y-4 text-sm text-night-700 leading-6">
          {TERMS_CONTENT_BLOCKS.map((block, blockIndex) => {
            if (block.type === 'spacer') {
              return <div key={blockIndex} aria-hidden="true" className="h-2" />;
            }

            const Component = block.type === 'heading' ? 'h3' : 'p';

            return (
              <Component
                key={blockIndex}
                className={cn(getTermsBlockClassName(block.type), 'whitespace-pre-wrap')}
              >
                {block.runs.map(renderTextRun)}
              </Component>
            );
          })}
        </div>
      </div>

      <div className="space-y-2 rounded-md border border-night-100 bg-lilac-100 p-3">
        <div className="flex items-start gap-3">
          <Checkbox
            id={checkboxId}
            checked={hasAcceptedTerms}
            onCheckedChange={checked => onAcceptedTermsChange(checked === true)}
            disabled={!hasScrolledToBottom || isSubmitting}
          />
          <label htmlFor={checkboxId} className="text-sm leading-5 text-night-900 cursor-pointer">
            {acceptanceLabel}
          </label>
        </div>
        {!hasScrolledToBottom && <p className="text-xs text-night-500">{helperText}</p>}
      </div>
    </>
  );
}
